import { Button, Modal, Select, message } from "antd";
import bem from "easy-bem";
import React, { useMemo, useState } from "react";

import type { Script, ScriptValues } from "../../data/types";
import { useRunScript } from "../../useRunScript";
import { useScripts } from "../../useScripts";

import ParamField from "./elements/ParamField";

import "./ScriptsRunner.css";

const cn = bem("scripts-runner");

// Значения формы по умолчанию берём из схемы скрипта.
const defaultValues = (script: Script): ScriptValues =>
  Object.fromEntries(
    script.params.map((param) => [param.name, param.default ?? undefined]),
  );

// Вкладка «Скрипты» (только админ): выбор скрипта наполнения БД, форма параметров
// генерируется из схемы, которую отдаёт бэкенд, и запуск с выводом результата.
const ScriptsRunner: React.FC = () => {
  const scripts = useScripts();
  const { mutateAsync, isPending } = useRunScript();

  const [selectedName, setSelectedName] = useState<string>();
  const [valuesByScript, setValuesByScript] = useState<
    Record<string, ScriptValues>
  >({});
  const [output, setOutput] = useState<string>("");

  const script = useMemo(
    () => scripts?.find((item) => item.name === selectedName) ?? scripts?.[0],
    [scripts, selectedName],
  );

  if (!scripts || !script) return null;

  const values = valuesByScript[script.name] ?? defaultValues(script);

  const update = (name: string, value: ScriptValues[string]) =>
    setValuesByScript((prev) => ({
      ...prev,
      [script.name]: { ...values, [name]: value },
    }));

  const run = async () => {
    try {
      const result = await mutateAsync({ name: script.name, params: values });
      setOutput(result.output);
      message.success("Скрипт выполнен");
    } catch (error) {
      setOutput("");
      message.error(error instanceof Error ? error.message : "Ошибка запуска");
    }
  };

  // Деструктивные параметры (reset) включены — просим подтверждение.
  const hasDestructiveOn = script.params.some(
    (param) => param.destructive && values[param.name] === true,
  );

  const handleRun = () => {
    if (!hasDestructiveOn) {
      run();

      return;
    }
    Modal.confirm({
      title: "Удалить и пересоздать данные?",
      content: `Скрипт ${script.name} с reset удалит существующие тестовые данные.`,
      okText: "Запустить",
      cancelText: "Отмена",
      okButtonProps: { danger: true },
      onOk: run,
    });
  };

  return (
    <div className={cn()}>
      <h3 className={cn("title")}>Скрипты наполнения БД</h3>

      <Select
        className={cn("select")}
        value={script.name}
        options={scripts.map((item) => ({
          value: item.name,
          label: item.label,
        }))}
        onChange={(name) => {
          setSelectedName(name);
          setOutput("");
        }}
      />
      <p className={cn("description")}>{script.description}</p>

      {script.params.map((param) => (
        <ParamField
          key={`${script.name}:${param.name}`}
          param={param}
          value={values[param.name]}
          onChange={(value) => update(param.name, value)}
        />
      ))}

      <Button
        type="primary"
        danger={hasDestructiveOn}
        loading={isPending}
        onClick={handleRun}
      >
        Запустить
      </Button>

      {output && <pre className={cn("output")}>{output}</pre>}
    </div>
  );
};

export default ScriptsRunner;
